/**
 * React Query reads around the turn-exact prompt-snapshot endpoints
 * (Task 15's backend, RBAC-gated by `prompt-snapshots:read` — a 403
 * surfaces as a query error, which callers treat the same as "no
 * snapshots" rather than crashing):
 *
 *   - `GET /api/personas/:id/prompt-snapshots?conversation_id=X` — the
 *     inspector's turn-picker list, newest-first. Never carries
 *     `system_text` (cheap to list).
 *   - `GET /api/personas/:id/prompt-snapshots/:snapshotId` — the full
 *     captured prompt for one turn, verbatim.
 *
 * `staleTime: Infinity` on both, same reasoning as `usePersonaComposedPrompt`:
 * a snapshot is captured once at render time and never changes, and the
 * list is scoped to one already-past conversation rather than live
 * activity (unlike `usePersonaRecentConversations`, which intentionally
 * uses a short staleTime).
 */
import { useQuery, type UseQueryResult } from '@tanstack/react-query'
import { apiClient } from '@/services/apiClient'
import type { PromptSnapshotDetail, PromptSnapshotListEntry } from '@/types/promptSnapshot'

interface ListArgs {
  personaId: string
  conversationId: string | null
  enabled: boolean
}

export function usePromptSnapshotList({
  personaId,
  conversationId,
  enabled,
}: ListArgs): UseQueryResult<PromptSnapshotListEntry[], Error> {
  return useQuery<PromptSnapshotListEntry[], Error>({
    queryKey: ['prompt-snapshots', personaId, conversationId],
    queryFn: () =>
      apiClient.get<PromptSnapshotListEntry[]>(
        `/api/personas/${personaId}/prompt-snapshots?conversation_id=${conversationId}`,
      ),
    // Requires a real conversation to scope the list to — same guard the
    // caller must apply, kept here too so this hook is safe to call
    // unconditionally.
    enabled: enabled && !!conversationId,
    staleTime: Infinity,
    refetchOnWindowFocus: false,
    refetchOnMount: false,
    retry: false,
  })
}

interface DetailArgs {
  personaId: string
  snapshotId: string | null
}

export function usePromptSnapshotDetail({
  personaId,
  snapshotId,
}: DetailArgs): UseQueryResult<PromptSnapshotDetail, Error> {
  return useQuery<PromptSnapshotDetail, Error>({
    queryKey: ['prompt-snapshot', personaId, snapshotId],
    queryFn: () =>
      apiClient.get<PromptSnapshotDetail>(
        `/api/personas/${personaId}/prompt-snapshots/${snapshotId}`,
      ),
    enabled: !!snapshotId,
    staleTime: Infinity,
    refetchOnWindowFocus: false,
    refetchOnMount: false,
    retry: false,
  })
}
